import { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Card, CardHeader, Btn, Input, Select, Table, fmt, Badge, PageHeader, POINT_VALUES } from '../components/ui';
import { Plus, Calculator, RefreshCw } from 'lucide-react';

const INIT = {
  customer_id: '', script_id: '', trade_date: new Date().toISOString().split('T')[0],
  lot_size: '', quantity: '', point_value: '0.10', price: '', remarks: ''
};

export default function BuyPage() {
  const [form, setForm] = useState(INIT);
  const [customers, setCustomers] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get('/masters/customers').then(r => setCustomers(r.data));
    api.get('/masters/scripts').then(r => setScripts(r.data));
    load();
  }, []);

  const load = () => api.get('/transactions', { params: { transaction_type: 'BUY' } }).then(r => setTransactions(r.data));

  // Auto-fill lot size from selected script
  const handleScriptChange = (e) => {
    const id = e.target.value;
    const script = scripts.find(s => s.id === parseInt(id));
    setForm(f => ({ ...f, script_id: id, lot_size: script ? script.lot_size : '' }));
  };

  // Live brokerage + lot preview
  useEffect(() => {
    const { customer_id, quantity, price, point_value, lot_size } = form;
    if (customer_id && quantity && price && point_value) {
      const cust = customers.find(c => c.id === parseInt(customer_id));
      const brokerageAmt = parseFloat(cust?.brokerage_amount || 0);
      const qty = parseInt(quantity) || 0;
      const pv = parseFloat(point_value) || 0;
      const pr = parseFloat(price) || 0;
      const ls = parseInt(lot_size) || 1;
      const grossAmount = qty * pr * pv;
      const netAmount = grossAmount + brokerageAmt;
      const totalLots = qty / ls;
      setPreview({ grossAmount, brokerageAmt, netAmount, totalLots, brokerPlan: cust?.plan_name });
    } else { setPreview(null); }
  }, [form, customers]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.script_id || !form.quantity || !form.price) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await api.post('/transactions', { ...form, transaction_type: 'BUY', quantity: parseInt(form.quantity), price: parseFloat(form.price), lot_size: parseInt(form.lot_size) });
      toast.success('Buy entry recorded!');
      setForm(INIT); setShowForm(false); setPreview(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  // Point values from selected script
  const selectedScript = scripts.find(s => s.id === parseInt(form.script_id));
  const pointValueOptions = selectedScript?.point_values || POINT_VALUES;

  return (
    <div>
      <PageHeader
        title="Buy Entry"
        subtitle="Record purchase transactions"
        action={<Btn onClick={() => setShowForm(v => !v)}><Plus size={15} /> New Buy</Btn>}
      />

      {showForm && (
        <Card style={{ marginBottom: 22 }}>
          <CardHeader title="New Buy Transaction" subtitle="Enter details — brokerage is calculated automatically from customer's plan" />
          <form onSubmit={submit} style={{ padding: 22 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 16 }}>
              <Select label="Customer *" value={form.customer_id} onChange={set('customer_id')}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.customer_code} — {c.customer_name}</option>)}
              </Select>
              <Select label="Script *" value={form.script_id} onChange={handleScriptChange}>
                <option value="">Select Script</option>
                {scripts.map(s => <option key={s.id} value={s.id}>{s.script_name} (Lot: {s.lot_size})</option>)}
              </Select>
              <Input label="Trade Date *" type="date" value={form.trade_date} onChange={set('trade_date')} />
              <Input label="Lot Size" type="number" min="1" value={form.lot_size} onChange={set('lot_size')} placeholder="Auto-filled from script" />
              <Input label="Quantity *" type="number" min="1" value={form.quantity} onChange={set('quantity')} placeholder="e.g. 100" />

              {/* Point Value — prominent selector */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Point Value *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {pointValueOptions.map(pv => (
                    <button key={pv} type="button" onClick={() => setForm(f => ({ ...f, point_value: pv }))}
                      style={{
                        flex: 1, padding: '9px 4px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', transition: 'all 0.15s',
                        background: form.point_value === pv ? 'linear-gradient(135deg,#2563eb,#1d4ed8)' : '#f8fafc',
                        color: form.point_value === pv ? '#fff' : '#374151',
                        border: form.point_value === pv ? 'none' : '1px solid #e2e8f0',
                        boxShadow: form.point_value === pv ? '0 2px 8px rgba(37,99,235,0.3)' : 'none'
                      }}>
                      {pv}
                    </button>
                  ))}
                </div>
              </div>

              <Input label="Price (₹) *" type="number" step="0.01" min="0.01" value={form.price} onChange={set('price')} placeholder="e.g. 250.50" />
              <Input label="Remarks" value={form.remarks} onChange={set('remarks')} placeholder="Optional" />
            </div>

            {/* Live Preview */}
            {preview && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: 18, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                  <Calculator size={15} color="#2563eb" />
                  <strong style={{ fontSize: 13, color: '#1e3a8a' }}>Transaction Preview</strong>
                  {preview.brokerPlan && <Badge color="blue">{preview.brokerPlan}</Badge>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
                  {[
                    ['Total Lots', parseFloat(preview.totalLots).toFixed(4)],
                    ['Gross Amount', fmt(preview.grossAmount)],
                    ['Brokerage', fmt(preview.brokerageAmt)],
                    ['Net Payable', fmt(preview.netAmount)],
                  ].map(([label, val]) => (
                    <div key={label} style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', border: '1px solid #bfdbfe' }}>
                      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3, fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{val}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', borderRadius: 9, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#fff', fontWeight: 600 }}>Net Amount to Pay</span>
                  <span style={{ color: '#fff', fontWeight: 900, fontSize: 20 }}>{fmt(preview.netAmount)}</span>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <Btn type="submit" variant="success" disabled={loading}>{loading ? 'Saving…' : 'Record Buy'}</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setForm(INIT); setPreview(null); }}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader title="Buy Transactions" subtitle={`${transactions.length} records`}
          action={<Btn variant="ghost" size="sm" onClick={load}><RefreshCw size={13} /></Btn>} />
        <Table
          headers={['Txn No', 'Date', 'Customer', 'Script', 'Lots', 'Qty', 'Point Val', 'Price', 'Gross', 'Brokerage', 'Net Payable']}
          rows={transactions.map(t => [
            <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#64748b' }}>{t.transaction_no}</span>,
            t.trade_date?.split('T')[0],
            <div><div style={{ fontWeight: 700 }}>{t.customer_code}</div><div style={{ fontSize: 11, color: '#64748b' }}>{t.customer_name}</div></div>,
            t.script_name,
            <Badge color="blue">{parseFloat(t.total_lots || 0).toFixed(2)}</Badge>,
            t.quantity,
            <Badge color="purple">{t.point_value}</Badge>,
            fmt(t.price),
            fmt(t.gross_amount),
            fmt(t.brokerage_amount),
            <strong style={{ color: '#1d4ed8' }}>{fmt(t.net_amount)}</strong>
          ])}
          empty="No buy transactions yet."
        />
      </Card>
    </div>
  );
}
